import React, { Fragment, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import pMap from 'p-map-browser';
import { useApolloClient } from 'react-apollo';
import uniqBy from 'lodash.uniqby';

import uuidv4 from '../../../util/uuid';
import { getErrorString } from '../../../util/normalize-errors';
import { isValidPhone } from '../../../util/phone';
import { isValidEmail } from '../../../util/email';
import Button from '../Button';
import Alert from '../Alert';
import PlusIcon from '../../icons/PlusIcon';
import Input from '../Input';
import CameraIcon from '../../icons/CameraIcon';
import ContactTile from './components/ContactTile';
import useAssetImageUpload from '../../../hooks/useAssetImageUpload';
import Spinner from '../Spinner';
import useCircles from '../../../apps/pro/views/Account/ContactCircles/hooks/useCircles';
import useContacts from './hooks/useContacts';
import { MeWithContacts } from '../../../graphql/queries/account';
import useOpenModal from '../../../hooks/useOpenModal';
import { CONTACT_CIRCLE_SHARE_MODAL } from '../ContactCircleShareModal';

const propTypes = {
  circleId: PropTypes.string,
  onRequestClose: PropTypes.func.isRequired,
  contactsOnly: PropTypes.bool,
};

const ContactCircleSave = ({ circleId, onRequestClose, contactsOnly }) => {
  const [name, setName] = useState('');
  const [assetImage, setAssetImage] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [deletedContactIds, setDeletedContactIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();
  const client = useApolloClient();
  const openModal = useOpenModal();

  const {
    circle,
    onCircleCreate: circleCreate,
    onCircleUpdate: circleUpdate,
  } = useCircles({ id: circleId });

  const { onContactCreate: contactCreate, onContactUpdate: contactUpdate } =
    useContacts();

  const { uploading, onUpload } = useAssetImageUpload({
    onImageUploaded: async uploadedAssetImage => {
      setAssetImage(uploadedAssetImage);
    },
  });

  useEffect(() => {
    setName(circle?.name);
    setAssetImage(circle?.asset_image);
    setContacts([
      ...(contactsOnly
        ? [
            {
              id: undefined,
              contact: {
                id: uuidv4(),
                first_name: '',
                last_name: '',
                phone_number: '',
                email_address: '',
                saved: false,
                is_email: false,
              },
            },
          ]
        : (circle?.contacts?.records ?? [
            {
              id: undefined,
              contact: {
                id: uuidv4(),
                first_name: '',
                last_name: '',
                phone_number: '',
                email_address: '',
                saved: false,
                is_email: false,
              },
            },
          ])
      ).map(({ id, contact }) => ({
        id: contact.id,
        circle_contact_id: id,
        name: `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim(),
        phone_or_email:
          contact.phones?.records?.[0]?.phone_number ??
          contact.emails?.records?.[0]?.email_address,
        phone_number_id: contact.phones?.records?.[0]?.id,
        email_address_id: contact.emails?.records?.[0]?.id,
        saved: contact?.saved ?? true,
        // In the rare case we have both email & phone (not possible via web but may exist from mobile)
        // then default to false, as overall phone number is considered default.
        is_email:
          !contact.phones?.records?.[0]?.id &&
          !!contact.emails?.records?.[0]?.id,
      })),
    ]);
  }, [circle]);

  const onAddContact = () => {
    setContacts([
      ...contacts,
      {
        id: uuidv4(),
        name: '',
        phone_or_email: '',
        saved: false,
        is_email: false,
      },
    ]);
  };

  const validateContacts = () => {
    if (contacts.length === 0) {
      setError('You must provide at least 1 contact.');
      return false;
    }
    if (contacts.find(contact => !contact.name)) {
      setError('One or more contacts are missing their name.');
      return false;
    }

    if (
      contacts.find(
        contact =>
          !(isValidPhone(contact.phone_or_email) && !contact.is_email) &&
          !(isValidEmail(contact.phone_or_email) && contact.is_email),
      )
    ) {
      setError(
        'One or more contacts have invalid phone numbers or email addresses.',
      );
      return false;
    }

    if (
      contacts.length >
      uniqBy(contacts, ({ phone_or_email }) => phone_or_email).length
    ) {
      setError(
        'Two or more contacts have the same phone number or email address.',
      );
      return false;
    }

    return true;
  };

  const createNewContact = async contact => {
    const [firstName, ...lastName] = contact.name.split(' ').map(s => s.trim());

    const isEmail = contact.is_email;

    const { data: { me } = {} } = await client.query({
      query: MeWithContacts,
      fetchPolicy: 'network-only',
      variables: {
        phone_number: !isEmail ? contact.phone_or_email : null,
        email_address: isEmail ? contact.phone_or_email : null,
      },
    });

    if ((me?.contacts?.total ?? 0) > 0) {
      const existentContact = me.contacts.records[0];
      const updatedContact = await contactUpdate({
        contactId: existentContact.id,
        firstName,
        lastName: lastName.join(' '),
      });

      return updatedContact?.id;
    }

    const createdContact = await contactCreate({
      firstName,
      lastName: lastName.join(' '),
      phoneNumber: !isEmail ? contact.phone_or_email : null,
      emailAddress: isEmail ? contact.phone_or_email : null,
    });

    return createdContact?.id;
  };

  const updateExistentContact = async contact => {
    const [firstName, ...lastName] = contact.name.split(' ').map(s => s.trim());
    // Since updating with the same values breaks things, we will only update if needed.
    // for this, we need the og contact.
    const circleContact = (circle.contacts?.records ?? []).find(
      c => c.contact.id === contact.id,
    );
    const originalPhone =
      circleContact?.contact?.phones?.records?.[0]?.phone_number;
    const originalEmail =
      circleContact?.contact?.emails?.records?.[0]?.email_address;

    const isEmail = contact.is_email;
    const shouldUpdatePhone =
      !isEmail && originalPhone !== contact.phone_or_email;
    const shouldUpdateEmail =
      isEmail && originalEmail !== contact.phone_or_email;

    const { data: { me } = {} } = await client.query({
      query: MeWithContacts,
      fetchPolicy: 'network-only',
      variables: {
        phone_number: !isEmail ? contact.phone_or_email : null,
        email_address: isEmail ? contact.phone_or_email : null,
      },
    });

    if (
      (me?.contacts?.total ?? 0) > 0 &&
      me.contacts.records[0].id !== contact.id
    ) {
      throw Error(
        `A different contact with the ${
          isEmail ? 'email address' : 'phone number'
        }: ${contact.phone_or_email} already exists.`,
      );
    }

    const updatedContact = await contactUpdate({
      contactId: contact.id,
      firstName,
      lastName: lastName.join(' '),
      phoneNumber: shouldUpdatePhone ? contact.phone_or_email : null,
      phoneNumberId: shouldUpdatePhone ? contact.phone_number_id : null,
      emailAddress: shouldUpdateEmail ? contact.phone_or_email : null,
      emailAddressId: shouldUpdateEmail ? contact.email_address_id : null,
    });

    return updatedContact?.id;
  };

  const onCreateCircle = async () => {
    const isValid = validateContacts();
    if (!isValid) {
      return;
    }

    try {
      setError('');
      setLoading(true);
      // First, we need to create the contacts.
      const contactIds = await pMap(contacts, createNewContact, {
        concurrency: 3,
      });

      const newCircle = await circleCreate({
        name,
        assetImageId: assetImage?.id,
        contactIds,
      });
      setLoading(false);
      openModal(CONTACT_CIRCLE_SHARE_MODAL, { circle: newCircle });
      onRequestClose();
    } catch (e) {
      setError(getErrorString(e));
    } finally {
      setLoading(false);
    }
  };

  const onUpdateCircle = async () => {
    const isValid = validateContacts();
    if (!isValid) {
      return;
    }

    try {
      setLoading(true);
      // Update the existent contacts
      await pMap(
        contacts.filter(({ saved }) => saved),
        updateExistentContact,
        {
          concurrency: 3,
        },
      );
      const newContactIds = await pMap(
        contacts.filter(({ saved }) => !saved),
        createNewContact,
        { concurrency: 3 },
      );
      await circleUpdate({
        circleId: circle.id,
        name: circle.name !== name ? name : undefined,
        assetImageId:
          assetImage?.id !== circle.asset_image?.id
            ? assetImage?.id
            : undefined,
        existingAssetImageId: circle.asset_image?.id,
        newContactIds,
        deletedContactIds,
      });
      setLoading(false);
      onRequestClose();
    } catch (e) {
      setError(getErrorString(e));
    } finally {
      setLoading(false);
    }
  };

  const onContactUpdate = (event, key, isEmail, contactId) => {
    setContacts(
      contacts.map(contact => {
        if (contact.id !== contactId) {
          return contact;
        }
        return {
          ...contact,
          [key]: event.target.value,
          is_email: isEmail,
        };
      }),
    );
  };

  return (
    <div className="p-0 p-md-3">
      {contactsOnly && <h3 className="text-center mb-3">Add contacts</h3>}
      {!contactsOnly && (
        <Fragment>
          {circle && (
            <h3 className="text-center mb-3">
              Updating <span className="text-primary">{circle.name}</span>{' '}
            </h3>
          )}
          {!circle && <h3 className="text-center mb-3">Create new circle</h3>}
          <div className="d-flex align-items-center mb-2">
            <Button
              btnStyle="reset"
              className="circle-photo-preview"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Spinner />
              ) : (
                <>
                  {assetImage ? (
                    <img
                      src={
                        assetImage.url ?? assetImage.thumbnail_urls?.[0]?.url
                      }
                      alt="Circle Preview"
                    />
                  ) : (
                    <CameraIcon
                      color="#798588"
                      size={24}
                      id="contact-circle-photo"
                    />
                  )}
                </>
              )}
            </Button>
            <input
              id="circle-asset-image"
              type="file"
              accept="image/*"
              onChange={event => {
                const [file] = event.target.files;
                onUpload(file);
              }}
              ref={ref => {
                fileInputRef.current = ref;
              }}
              hidden
            />
            <Input
              id="contact-circle-name"
              name="contactCircleName"
              className="ml-2"
              placeholder="Name your circle"
              value={name}
              onChange={({ target }) => setName(target.value)}
            />
          </div>
          <p className="text-light text-extra-bold text-sm text-uppercase">
            Add Contacts to Circle
          </p>
        </Fragment>
      )}

      {contacts.map(contact => (
        <ContactTile
          key={contact.id}
          contact={contact}
          onChange={onContactUpdate}
          onRemove={() => {
            setContacts(contacts.filter(({ id }) => contact.id !== id));
            if (contact.saved) {
              setDeletedContactIds([
                ...deletedContactIds,
                contact.circle_contact_id,
              ]);
            }
          }}
          onEnter={() => onAddContact()}
        />
      ))}
      <Button
        btnStyle="link"
        className="d-flex align-items-center mt-2"
        data-testid="add-contact-btn"
        onClick={onAddContact}
      >
        <PlusIcon color="#0d84fe" size={14} className="mr-1" /> Add another
        contact
      </Button>
      {error && (
        <Alert
          type="danger"
          dismissible
          onDismiss={() => setError('')}
          className="w-100 mt-2"
        >
          {error}
        </Alert>
      )}
      <Button
        btnBlock
        data-testid="contact-circle-save-btn"
        onClick={() => {
          if (circle?.id) {
            onUpdateCircle();
          } else {
            onCreateCircle();
          }
        }}
        disabled={loading || !name || contacts.length === 0}
        className="mt-4"
      >
        {loading ? 'Saving...' : 'Save'}
      </Button>
    </div>
  );
};

ContactCircleSave.propTypes = propTypes;

export { ContactCircleSave as ContactCircleSaveComp };

export default ContactCircleSave;
